import { lazy } from 'react';
import { Navigate, useParams, type RouteObject } from 'react-router-dom';

const DashboardPage = lazy(() => import('../features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const HomeHubPage = lazy(() => import('../features/home').then((m) => ({ default: m.HomeHubPage })));
const PortfolioSearchPage = lazy(() => import('../features/portfolios').then((m) => ({ default: m.PortfolioSearchPage })));
const PortfolioDetailPage = lazy(() => import('../features/portfolios').then((m) => ({ default: m.PortfolioDetailPage })));
const CreatePortfolioPage = lazy(() => import('../features/portfolios').then((m) => ({ default: m.CreatePortfolioPage })));
const EditPortfolioPage = lazy(() => import('../features/portfolios').then((m) => ({ default: m.EditPortfolioPage })));
const WealthNodeLandingPage = lazy(() => import('../features/portfolios').then((m) => ({ default: m.WealthNodeLandingPage })));
const AccountSearchPage = lazy(() => import('../features/accounts').then((m) => ({ default: m.AccountSearchPage })));
const AccountDetailPage = lazy(() => import('../features/accounts').then((m) => ({ default: m.AccountDetailPage })));
const OpenAccountPage = lazy(() => import('../features/accounts').then((m) => ({ default: m.OpenAccountPage })));
const EditAccountPage = lazy(() => import('../features/accounts').then((m) => ({ default: m.EditAccountPage })));
const CustodianSearchPage = lazy(() => import('../features/custodians').then((m) => ({ default: m.CustodianSearchPage })));
const CustodianDetailPage = lazy(() => import('../features/custodians').then((m) => ({ default: m.CustodianDetailPage })));
const CreateCustodianPage = lazy(() => import('../features/custodians').then((m) => ({ default: m.CreateCustodianPage })));
const EditCustodianPage = lazy(() => import('../features/custodians').then((m) => ({ default: m.EditCustodianPage })));
const ExchangeSearchPage = lazy(() => import('../features/exchanges').then((m) => ({ default: m.ExchangeSearchPage })));
const ExchangeDetailPage = lazy(() => import('../features/exchanges').then((m) => ({ default: m.ExchangeDetailPage })));
const CreateExchangePage = lazy(() => import('../features/exchanges').then((m) => ({ default: m.CreateExchangePage })));
const ClientSearchPage = lazy(() => import('../features/clients').then((m) => ({ default: m.ClientSearchPage })));
const ClientDetailPage = lazy(() => import('../features/clients').then((m) => ({ default: m.ClientDetailPage })));
const CreateClientPage = lazy(() => import('../features/clients').then((m) => ({ default: m.CreateClientPage })));
const OnboardingDashboardPage = lazy(() =>
  import('../features/clientOnboarding').then((m) => ({ default: m.OnboardingDashboardPage }))
);
const OnboardingPipelinePage = lazy(() =>
  import('../features/clientOnboarding').then((m) => ({ default: m.OnboardingPipelinePage }))
);
const OnboardingAgingPage = lazy(() =>
  import('../features/clientOnboarding').then((m) => ({ default: m.OnboardingAgingPage }))
);
const ScreeningOutcomesPage = lazy(() =>
  import('../features/clientOnboarding').then((m) => ({ default: m.ScreeningOutcomesPage }))
);
const DocumentsStatusPage = lazy(() =>
  import('../features/clientOnboarding').then((m) => ({ default: m.DocumentsStatusPage }))
);
const KycDocumentVerificationPage = lazy(() =>
  import('../features/clientOnboarding').then((m) => ({ default: m.KycDocumentVerificationPage }))
);
const ComplianceScreeningQueuePage = lazy(() =>
  import('../features/clientOnboarding').then((m) => ({ default: m.ComplianceScreeningQueuePage }))
);
const OnboardingReviewQueuePage = lazy(() =>
  import('../features/clientOnboarding').then((m) => ({ default: m.OnboardingReviewQueuePage }))
);
const RiskQuestionnaireTemplateLibraryPage = lazy(() =>
  import('../features/riskQuestionnaireTemplates').then((m) => ({
    default: m.RiskQuestionnaireTemplateLibraryPage,
  }))
);
const OnboardClientPage = lazy(() =>
  import('../features/onboarding').then((m) => ({ default: m.OnboardClientPage }))
);
const BeneficialOwnershipDiagramPage = lazy(() =>
  import('../features/beneficialOwnership').then((m) => ({ default: m.BeneficialOwnershipDiagramPage }))
);
const OwnershipDiagramEditorPage = lazy(() =>
  import('../features/ownershipDiagramEditor').then((m) => ({ default: m.OwnershipDiagramEditorPage }))
);
const InstrumentSearchPage = lazy(() => import('../features/instruments').then((m) => ({ default: m.InstrumentSearchPage })));
const InstrumentDetailPage = lazy(() => import('../features/instruments').then((m) => ({ default: m.InstrumentDetailPage })));
const EditInstrumentPage = lazy(() => import('../features/instruments').then((m) => ({ default: m.EditInstrumentPage })));
const DefineEquityPage = lazy(() => import('../features/instruments').then((m) => ({ default: m.DefineEquityPage })));
const DefineCurrencyPage = lazy(() => import('../features/instruments').then((m) => ({ default: m.DefineCurrencyPage })));
const DefineFundPage = lazy(() => import('../features/instruments').then((m) => ({ default: m.DefineFundPage })));
const DefineFixedRateBondPage = lazy(() =>
  import('../features/instruments').then((m) => ({ default: m.DefineFixedRateBondPage }))
);
const DefineZeroCouponBondPage = lazy(() =>
  import('../features/instruments').then((m) => ({ default: m.DefineZeroCouponBondPage }))
);
const DefineFloatingRateBondPage = lazy(() =>
  import('../features/instruments').then((m) => ({ default: m.DefineFloatingRateBondPage }))
);
const DefineCommodityPage = lazy(() => import('../features/instruments').then((m) => ({ default: m.DefineCommodityPage })));
const DefineFxCurrencyPairPage = lazy(() =>
  import('../features/instruments').then((m) => ({ default: m.DefineFxCurrencyPairPage }))
);
const DefineRealAssetPage = lazy(() =>
  import('../features/realAssets').then((m) => ({ default: m.DefineRealAssetPage }))
);
const RealAssetsDashboardPage = lazy(() =>
  import('../features/realAssets').then((m) => ({ default: m.RealAssetsDashboardPage }))
);
const RealAssetDetailPage = lazy(() =>
  import('../features/realAssets').then((m) => ({ default: m.RealAssetDetailPage }))
);
const AcquireRealAssetPage = lazy(() =>
  import('../features/realAssets').then((m) => ({ default: m.AcquireRealAssetPage }))
);
const SellRealAssetPage = lazy(() =>
  import('../features/realAssets').then((m) => ({ default: m.SellRealAssetPage }))
);
const GiftRealAssetPage = lazy(() =>
  import('../features/realAssets').then((m) => ({ default: m.GiftRealAssetPage }))
);
const InheritRealAssetPage = lazy(() =>
  import('../features/realAssets').then((m) => ({ default: m.InheritRealAssetPage }))
);
const CatalogueRealAssetPage = lazy(() =>
  import('../features/realAssets').then((m) => ({ default: m.CatalogueRealAssetPage }))
);
const RecordRealAssetAppraisalPage = lazy(() =>
  import('../features/realAssets').then((m) => ({ default: m.RecordRealAssetAppraisalPage }))
);
const EncumberRealAssetPage = lazy(() =>
  import('../features/realAssets').then((m) => ({ default: m.EncumberRealAssetPage }))
);
const ReleaseRealAssetEncumbrancePage = lazy(() =>
  import('../features/realAssets').then((m) => ({ default: m.ReleaseRealAssetEncumbrancePage }))
);
const RecordCapitalImprovementPage = lazy(() =>
  import('../features/realAssets').then((m) => ({ default: m.RecordCapitalImprovementPage }))
);
const RecordDistributionReceivedPage = lazy(() =>
  import('../features/realAssets').then((m) => ({ default: m.RecordDistributionReceivedPage }))
);
const EnterOperatingStagePage = lazy(() =>
  import('../features/realAssets').then((m) => ({ default: m.EnterOperatingStagePage }))
);
const DefineTimeDepositPage = lazy(() =>
  import('../features/instruments').then((m) => ({ default: m.DefineTimeDepositPage }))
);
const DefineLoanPage = lazy(() =>
  import('../features/instruments').then((m) => ({ default: m.DefineLoanPage }))
);
const DefineIndexPage = lazy(() =>
  import('../features/instruments').then((m) => ({ default: m.DefineIndexPage }))
);
const DefineIndexFuturesPage = lazy(() =>
  import('../features/instruments').then((m) => ({ default: m.DefineIndexFuturesPage }))
);
const DefineStockFuturesPage = lazy(() =>
  import('../features/instruments').then((m) => ({ default: m.DefineStockFuturesPage }))
);
const DefineCurrencyFuturesPage = lazy(() =>
  import('../features/instruments').then((m) => ({ default: m.DefineCurrencyFuturesPage }))
);
const DefineCommodityFuturesPage = lazy(() =>
  import('../features/instruments').then((m) => ({ default: m.DefineCommodityFuturesPage }))
);
const DefineIndexOptionPage = lazy(() =>
  import('../features/instruments').then((m) => ({ default: m.DefineIndexOptionPage }))
);
const DefineStockOptionPage = lazy(() =>
  import('../features/instruments').then((m) => ({ default: m.DefineStockOptionPage }))
);
const DefineOptionOnFuturesPage = lazy(() =>
  import('../features/instruments').then((m) => ({ default: m.DefineOptionOnFuturesPage }))
);
const DefineFxOptionPage = lazy(() =>
  import('../features/instruments').then((m) => ({ default: m.DefineFxOptionPage }))
);
const CaptureBuyPage = lazy(() => import('../features/deals').then((m) => ({ default: m.CaptureBuyPage })));
const CaptureSellPage = lazy(() => import('../features/deals').then((m) => ({ default: m.CaptureSellPage })));
const CaptureDepositPage = lazy(() => import('../features/deals').then((m) => ({ default: m.CaptureDepositPage })));
const CaptureWithdrawPage = lazy(() => import('../features/deals').then((m) => ({ default: m.CaptureWithdrawPage })));
const CaptureFxSpotPage = lazy(() => import('../features/deals').then((m) => ({ default: m.CaptureFxSpotPage })));
const CaptureFxForwardPage = lazy(() => import('../features/deals').then((m) => ({ default: m.CaptureFxForwardPage })));
const CaptureIncomePage = lazy(() => import('../features/deals').then((m) => ({ default: m.CaptureIncomePage })));
const CaptureGiftPage = lazy(() => import('../features/deals').then((m) => ({ default: m.CaptureGiftPage })));
const CaptureInheritancePage = lazy(() =>
  import('../features/deals').then((m) => ({ default: m.CaptureInheritancePage }))
);
const CaptureExpensePage = lazy(() => import('../features/deals').then((m) => ({ default: m.CaptureExpensePage })));
const ReceiveLongSecurityPage = lazy(() =>
  import('../features/deals').then((m) => ({ default: m.ReceiveLongSecurityPage }))
);
const ReceiveShortSecurityPage = lazy(() =>
  import('../features/deals').then((m) => ({ default: m.ReceiveShortSecurityPage }))
);
const DeliverLongSecurityPage = lazy(() =>
  import('../features/deals').then((m) => ({ default: m.DeliverLongSecurityPage }))
);
const DeliverShortSecurityPage = lazy(() =>
  import('../features/deals').then((m) => ({ default: m.DeliverShortSecurityPage }))
);
const CaptureBuyOtcOptionPage = lazy(() =>
  import('../features/deals').then((m) => ({ default: m.CaptureBuyOtcOptionPage }))
);
const CaptureSellOtcOptionPage = lazy(() =>
  import('../features/deals').then((m) => ({ default: m.CaptureSellOtcOptionPage }))
);
const PlaceTermDepositPage = lazy(() =>
  import('../features/deals').then((m) => ({ default: m.PlaceTermDepositPage }))
);
const BreakTermDepositPage = lazy(() =>
  import('../features/deals').then((m) => ({ default: m.BreakTermDepositPage }))
);
const DrawdownLoanPage = lazy(() =>
  import('../features/deals').then((m) => ({ default: m.DrawdownLoanPage }))
);
const RepayLoanEarlyPage = lazy(() =>
  import('../features/deals').then((m) => ({ default: m.RepayLoanEarlyPage }))
);
const DealSearchPage = lazy(() => import('../features/deals').then((m) => ({ default: m.DealSearchPage })));
const DealDetailPage = lazy(() => import('../features/deals').then((m) => ({ default: m.DealDetailPage })));
const AiDealCapturePage = lazy(() =>
  import('../features/deals').then((m) => ({ default: m.AiDealCapturePage }))
);
const WpfNavPlaceholderPage = lazy(() =>
  import('../features/placeholder').then((m) => ({ default: m.WpfNavPlaceholderPage }))
);
const ThemeSelectionPage = lazy(() =>
  import('../features/settings').then((m) => ({ default: m.ThemeSelectionPage }))
);
const SignalRLogsPage = lazy(() =>
  import('../features/system').then((m) => ({ default: m.SignalRLogsPage }))
);
const NotificationHistoryPage = lazy(() =>
  import('../features/system').then((m) => ({ default: m.NotificationHistoryPage }))
);
const HoldingsPositionsReportPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.HoldingsPositionsReportPage }))
);
const TransactionsCashDashboardPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.TransactionsCashDashboardPage }))
);
const PerformanceDashboardPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.PerformanceDashboardPage }))
);
const PLDashboardPage = lazy(() => import('../features/reports').then((m) => ({ default: m.PLDashboardPage })));
const AnalyticsDashboardPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.AnalyticsDashboardPage }))
);
const ReconciliationDashboardPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.ReconciliationDashboardPage }))
);
const UnifiedDashboardPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.UnifiedDashboardPage }))
);
const PrivateEquityDashboardPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.PrivateEquityDashboardPage }))
);
const CashPositionDashboardPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.CashPositionDashboardPage }))
);
const BalanceTimelineDashboardPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.BalanceTimelineDashboardPage }))
);
const PortfolioValueTimelineDashboardPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.PortfolioValueTimelineDashboardPage }))
);
const SearchParticipationsPage = lazy(() =>
  import('../features/privateEquity').then((m) => ({ default: m.SearchParticipationsPage }))
);
const DefinePrivateEquityPage = lazy(() =>
  import('../features/privateEquity').then((m) => ({ default: m.DefinePrivateEquityPage }))
);
const CapturePrivateEquityActivityPage = lazy(() =>
  import('../features/privateEquity').then((m) => ({ default: m.CapturePrivateEquityActivityPage }))
);
const ReceivePrivateEquityPage = lazy(() =>
  import('../features/privateEquity').then((m) => ({ default: m.ReceivePrivateEquityPage }))
);
const PrivateEquityParticipationDetailPage = lazy(() =>
  import('../features/privateEquity').then((m) => ({ default: m.PrivateEquityParticipationDetailPage }))
);
const DynamicDashboardPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.DynamicDashboardPage }))
);
const ReconciliationWorkbenchPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.ReconciliationWorkbenchPage }))
);
const WhatIfScenarioDashboardPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.WhatIfScenarioDashboardPage }))
);
const UserDashboardListPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.UserDashboardListPage }))
);
const DashboardDesignerPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.DashboardDesignerPage }))
);
const ConfigurableReportPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.ConfigurableReportPage }))
);
const ClientStatementDashboardPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.ClientStatementDashboardPage }))
);
const ReportExplorerPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.ReportExplorerPage }))
);
const PLAnalyticsPage = lazy(() => import('../features/reports').then((m) => ({ default: m.PLAnalyticsPage })));
const PositionPerformancePage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.PositionPerformancePage }))
);
const TermDepositsReportPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.TermDepositsReportPage }))
);
const LoansReportPage = lazy(() => import('../features/reports').then((m) => ({ default: m.LoansReportPage })));
const PLAnalyticsRollupPage = lazy(() =>
  import('../features/reports').then((m) => ({ default: m.PLAnalyticsRollupPage }))
);
const MarketPricesBrowserPage = lazy(() =>
  import('../features/marketData').then((m) => ({ default: m.MarketPricesBrowserPage }))
);
const ForexRatesBrowserPage = lazy(() =>
  import('../features/marketData').then((m) => ({ default: m.ForexRatesBrowserPage }))
);
const CapturePricePage = lazy(() =>
  import('../features/marketData').then((m) => ({ default: m.CapturePricePage }))
);
const CaptureFxRatePage = lazy(() =>
  import('../features/marketData').then((m) => ({ default: m.CaptureFxRatePage }))
);
const PositionPricesPage = lazy(() =>
  import('../features/marketData').then((m) => ({ default: m.PositionPricesPage }))
);
const CaptureCashDividendPage = lazy(() =>
  import('../features/corporateActions').then((m) => ({ default: m.CaptureCashDividendPage }))
);
const CaptureStockDividendPage = lazy(() =>
  import('../features/corporateActions').then((m) => ({ default: m.CaptureStockDividendPage }))
);
const CaptureReturnOfCapitalPage = lazy(() =>
  import('../features/corporateActions').then((m) => ({ default: m.CaptureReturnOfCapitalPage }))
);
const CaptureCapitalGainsDistributionPage = lazy(() =>
  import('../features/corporateActions').then((m) => ({ default: m.CaptureCapitalGainsDistributionPage }))
);
const CaptureStockSplitPage = lazy(() =>
  import('../features/corporateActions').then((m) => ({ default: m.CaptureStockSplitPage }))
);
const CaptureReverseStockSplitPage = lazy(() =>
  import('../features/corporateActions').then((m) => ({ default: m.CaptureReverseStockSplitPage }))
);
const CaptureMergerPage = lazy(() =>
  import('../features/corporateActions').then((m) => ({ default: m.CaptureMergerPage }))
);
const CaptureShareClassConversionPage = lazy(() =>
  import('../features/corporateActions').then((m) => ({ default: m.CaptureShareClassConversionPage }))
);
const CaptureSpinOffPage = lazy(() =>
  import('../features/corporateActions').then((m) => ({ default: m.CaptureSpinOffPage }))
);
const SearchCorporateActionsPage = lazy(() =>
  import('../features/corporateActions').then((m) => ({ default: m.SearchCorporateActionsPage }))
);
const FeeRulesPage = lazy(() => import('../features/fees').then((m) => ({ default: m.FeeRulesPage })));
const CreateFeeRulePage = lazy(() => import('../features/fees').then((m) => ({ default: m.CreateFeeRulePage })));
const FeeDeviationReportPage = lazy(() =>
  import('../features/fees').then((m) => ({ default: m.FeeDeviationReportPage }))
);
const ReclaimableChargesReportPage = lazy(() =>
  import('../features/fees').then((m) => ({ default: m.ReclaimableChargesReportPage }))
);
const RetrocessionReportPage = lazy(() =>
  import('../features/fees').then((m) => ({ default: m.RetrocessionReportPage }))
);
const ComplianceDashboardPage = lazy(() =>
  import('../features/compliance').then((m) => ({ default: m.ComplianceDashboardPage }))
);
const ComplianceRulesDashboardPage = lazy(() =>
  import('../features/compliance').then((m) => ({ default: m.ComplianceRulesDashboardPage }))
);
const CreateComplianceRulePage = lazy(() =>
  import('../features/compliance').then((m) => ({ default: m.CreateComplianceRulePage }))
);
const CreateCompliancePolicyPage = lazy(() =>
  import('../features/compliance').then((m) => ({ default: m.CreateCompliancePolicyPage }))
);
const CompliancePolicyDetailPage = lazy(() =>
  import('../features/compliance').then((m) => ({ default: m.CompliancePolicyDetailPage }))
);
const SuggestComplianceRulesPage = lazy(() =>
  import('../features/compliance').then((m) => ({ default: m.SuggestComplianceRulesPage }))
);
const ClassificationSchemesPage = lazy(() =>
  import('../features/classifications').then((m) => ({ default: m.ClassificationSchemesPage }))
);
const DefineCompositeBenchmarkPage = lazy(() =>
  import('../features/benchmarks').then((m) => ({ default: m.DefineCompositeBenchmarkPage }))
);
const AssetClassBenchmarksPage = lazy(() =>
  import('../features/benchmarks').then((m) => ({ default: m.AssetClassBenchmarksPage }))
);
const FuturesContractSearchPage = lazy(() =>
  import('../features/instruments').then((m) => ({ default: m.FuturesContractSearchPage }))
);
const OptionChainSearchPage = lazy(() =>
  import('../features/instruments').then((m) => ({ default: m.OptionChainSearchPage }))
);

function RedirectLegacyNbaInstrument() {
  const { instrumentId } = useParams();
  return <Navigate to={instrumentId ? `/realassets/${instrumentId}` : '/reports/dashboards/real-assets'} replace />;
}

/** Absolute-path route table for document panels (`matchRoutes` / `renderMatches`). */
export const shellRouteObjects: RouteObject[] = [
  { path: '/', element: <DashboardPage /> },
  { path: '/home', element: <HomeHubPage /> },
  { path: '/holdings', element: <Navigate to="/reports/dashboards/holdings-positions" replace /> },
  { path: '/dashboard/cash-position', element: <CashPositionDashboardPage /> },
  { path: '/dashboard/balance-timeline', element: <BalanceTimelineDashboardPage /> },
  { path: '/dashboard/portfolio-value-timeline', element: <PortfolioValueTimelineDashboardPage /> },

  { path: '/portfolios', element: <PortfolioSearchPage /> },
  { path: '/portfolios/new', element: <CreatePortfolioPage /> },
  { path: '/portfolios/:portfolioId/edit', element: <EditPortfolioPage /> },
  { path: '/portfolios/:portfolioId/wealth', element: <WealthNodeLandingPage /> },
  { path: '/portfolios/:portfolioId', element: <PortfolioDetailPage /> },
  { path: '/accounts', element: <AccountSearchPage /> },
  { path: '/accounts/new', element: <OpenAccountPage /> },
  { path: '/accounts/:accountId/edit', element: <EditAccountPage /> },
  { path: '/accounts/:accountId', element: <AccountDetailPage /> },
  { path: '/custodians', element: <CustodianSearchPage /> },
  { path: '/custodians/new', element: <CreateCustodianPage /> },
  { path: '/custodians/:custodianId/edit', element: <EditCustodianPage /> },
  { path: '/custodians/:custodianId', element: <CustodianDetailPage /> },

  { path: '/exchanges', element: <ExchangeSearchPage /> },
  { path: '/exchanges/new', element: <CreateExchangePage /> },
  { path: '/exchanges/:exchangeId', element: <ExchangeDetailPage /> },
  { path: '/clients', element: <ClientSearchPage /> },
  { path: '/clients/new', element: <CreateClientPage /> },
  { path: '/clients/onboard', element: <OnboardClientPage /> },
  { path: '/clients/beneficial-ownership', element: <BeneficialOwnershipDiagramPage /> },
  { path: '/clients/ownership-diagram-editor', element: <OwnershipDiagramEditorPage /> },
  { path: '/clients/onboarding/dashboard', element: <OnboardingDashboardPage /> },
  { path: '/clients/onboarding/pipeline', element: <OnboardingPipelinePage /> },
  { path: '/clients/onboarding/aging', element: <OnboardingAgingPage /> },
  { path: '/clients/onboarding/screening-outcomes', element: <ScreeningOutcomesPage /> },
  { path: '/clients/onboarding/documents-status', element: <DocumentsStatusPage /> },
  { path: '/clients/kyc/document-verification', element: <KycDocumentVerificationPage /> },
  { path: '/clients/compliance/screening-queue', element: <ComplianceScreeningQueuePage /> },
  { path: '/clients/onboarding/review-queue', element: <OnboardingReviewQueuePage /> },
  { path: '/clients/risk-questionnaire-templates', element: <RiskQuestionnaireTemplateLibraryPage /> },
  { path: '/clients/:clientId', element: <ClientDetailPage /> },

  { path: '/instruments/new/equity', element: <DefineEquityPage /> },
  { path: '/instruments/new/currency', element: <DefineCurrencyPage /> },
  { path: '/instruments/new/fund', element: <DefineFundPage /> },
  { path: '/instruments/new/bond', element: <DefineFixedRateBondPage /> },
  { path: '/instruments/new/bond/zero-coupon', element: <DefineZeroCouponBondPage /> },
  { path: '/instruments/new/bond/floating-rate', element: <DefineFloatingRateBondPage /> },
  { path: '/instruments/new/commodity', element: <DefineCommodityPage /> },
  { path: '/instruments/new/nba', element: <Navigate to="/realassets/new" replace /> },
  { path: '/instruments/new/fx-pair', element: <DefineFxCurrencyPairPage /> },
  { path: '/instruments/new/time-deposit', element: <DefineTimeDepositPage /> },
  { path: '/instruments/new/loan', element: <DefineLoanPage /> },
  { path: '/instruments/new/index', element: <DefineIndexPage /> },
  { path: '/instruments/new/index-futures', element: <DefineIndexFuturesPage /> },
  { path: '/instruments/new/stock-futures', element: <DefineStockFuturesPage /> },
  { path: '/instruments/new/currency-futures', element: <DefineCurrencyFuturesPage /> },
  { path: '/instruments/new/commodity-futures', element: <DefineCommodityFuturesPage /> },
  { path: '/instruments/new/index-option', element: <DefineIndexOptionPage /> },
  { path: '/instruments/new/stock-option', element: <DefineStockOptionPage /> },
  { path: '/instruments/new/option-on-futures', element: <DefineOptionOnFuturesPage /> },
  { path: '/instruments/new/fx-option', element: <DefineFxOptionPage /> },
  { path: '/instruments/classification-schemes', element: <ClassificationSchemesPage /> },
  { path: '/instruments/search/futures-contracts', element: <FuturesContractSearchPage /> },
  { path: '/instruments/search/option-chains', element: <OptionChainSearchPage /> },
  { path: '/instruments', element: <InstrumentSearchPage /> },
  { path: '/instruments/nba/:instrumentId', element: <RedirectLegacyNbaInstrument /> },
  { path: '/instruments/:instrumentId/edit', element: <EditInstrumentPage /> },
  { path: '/instruments/:instrumentId', element: <InstrumentDetailPage /> },

  { path: '/benchmarks/composite', element: <DefineCompositeBenchmarkPage /> },
  { path: '/benchmarks/asset-class', element: <AssetClassBenchmarksPage /> },

  { path: '/deals/search', element: <DealSearchPage /> },
  { path: '/deals/:dealId', element: <DealDetailPage /> },
  { path: '/deals/capture/buy', element: <CaptureBuyPage /> },
  { path: '/deals/capture/sell', element: <CaptureSellPage /> },
  { path: '/deals/capture/deposit', element: <CaptureDepositPage /> },
  { path: '/deals/capture/withdraw', element: <CaptureWithdrawPage /> },
  { path: '/deals/capture/fx-spot', element: <CaptureFxSpotPage /> },
  { path: '/deals/capture/fx-forward', element: <CaptureFxForwardPage /> },
  { path: '/deals/capture/income', element: <CaptureIncomePage /> },
  { path: '/deals/capture/gift', element: <CaptureGiftPage /> },
  { path: '/deals/capture/inheritance', element: <CaptureInheritancePage /> },
  { path: '/deals/capture/expense', element: <CaptureExpensePage /> },
  { path: '/deals/capture/receive-long', element: <ReceiveLongSecurityPage /> },
  { path: '/deals/capture/receive-short', element: <ReceiveShortSecurityPage /> },
  { path: '/deals/capture/deliver-long', element: <DeliverLongSecurityPage /> },
  { path: '/deals/capture/deliver-short', element: <DeliverShortSecurityPage /> },
  { path: '/deals/capture/otc-option/buy', element: <CaptureBuyOtcOptionPage /> },
  { path: '/deals/capture/otc-option/sell', element: <CaptureSellOtcOptionPage /> },
  { path: '/deals/capture/term-deposit/place', element: <PlaceTermDepositPage /> },
  { path: '/deals/capture/term-deposit/break', element: <BreakTermDepositPage /> },
  { path: '/deals/capture/loan/drawdown', element: <DrawdownLoanPage /> },
  { path: '/deals/capture/loan/repay', element: <RepayLoanEarlyPage /> },
  { path: '/deals/capture/nba/acquire', element: <Navigate to="/realassets/acquire" replace /> },
  { path: '/deals/capture/nba/dispose', element: <Navigate to="/realassets/sell" replace /> },
  { path: '/deals/capture/nba/gift', element: <Navigate to="/realassets/gift" replace /> },
  { path: '/deals/capture/nba/inheritance', element: <Navigate to="/realassets/inheritance" replace /> },
  { path: '/realassets/new', element: <DefineRealAssetPage /> },
  { path: '/realassets/acquire', element: <AcquireRealAssetPage /> },
  { path: '/realassets/sell', element: <SellRealAssetPage /> },
  { path: '/realassets/gift', element: <GiftRealAssetPage /> },
  { path: '/realassets/inheritance', element: <InheritRealAssetPage /> },
  { path: '/realassets/:assetId/catalogue', element: <CatalogueRealAssetPage /> },
  { path: '/realassets/:assetId/appraisal', element: <RecordRealAssetAppraisalPage /> },
  { path: '/realassets/:assetId/encumber', element: <EncumberRealAssetPage /> },
  { path: '/realassets/:assetId/release', element: <ReleaseRealAssetEncumbrancePage /> },
  { path: '/realassets/:assetId/improvement', element: <RecordCapitalImprovementPage /> },
  { path: '/realassets/:assetId/distribution', element: <RecordDistributionReceivedPage /> },
  { path: '/realassets/:assetId/operating-stage', element: <EnterOperatingStagePage /> },
  { path: '/realassets/:assetId/acquire', element: <AcquireRealAssetPage /> },
  { path: '/realassets/:assetId/gift', element: <GiftRealAssetPage /> },
  { path: '/realassets/:assetId/inheritance', element: <InheritRealAssetPage /> },
  { path: '/realassets/:assetId/sell', element: <SellRealAssetPage /> },
  { path: '/realassets/:assetId', element: <RealAssetDetailPage /> },
  { path: '/deals/capture/ai', element: <AiDealCapturePage /> },

  { path: '/reports/dashboards/holdings-positions', element: <HoldingsPositionsReportPage /> },
  { path: '/reports/dashboards/performance', element: <PerformanceDashboardPage /> },
  { path: '/reports/dashboards/pl', element: <PLDashboardPage /> },
  { path: '/reports/dashboards/transactions-cash', element: <TransactionsCashDashboardPage /> },
  { path: '/reports/dashboards/analytics', element: <AnalyticsDashboardPage /> },
  { path: '/reports/dashboards/reconciliation', element: <ReconciliationDashboardPage /> },
  { path: '/reports/dashboards/reconciliation-workbench', element: <ReconciliationWorkbenchPage /> },
  { path: '/reports/dashboards/what-if', element: <WhatIfScenarioDashboardPage /> },
  { path: '/reports/dashboards/unified', element: <UnifiedDashboardPage /> },
  { path: '/reports/dashboards/private-equity', element: <PrivateEquityDashboardPage /> },
  { path: '/reports/dashboards/real-assets', element: <RealAssetsDashboardPage /> },
  { path: '/reports/dashboards/dynamic', element: <DynamicDashboardPage /> },
  { path: '/reports/dashboards/my', element: <UserDashboardListPage /> },
  { path: '/reports/dashboards/designer', element: <DashboardDesignerPage /> },
  { path: '/reports/configurable', element: <ConfigurableReportPage /> },
  { path: '/reports/dashboards/client-statement', element: <ClientStatementDashboardPage /> },
  { path: '/reports/explorer', element: <ReportExplorerPage /> },
  { path: '/reports/pl-analytics', element: <PLAnalyticsPage /> },
  { path: '/reports/position-performance', element: <PositionPerformancePage /> },
  { path: '/reports/term-deposits', element: <TermDepositsReportPage /> },
  { path: '/reports/loans', element: <LoansReportPage /> },
  { path: '/reports/pl-analytics-summary', element: <PLAnalyticsRollupPage /> },

  { path: '/private-equity/participations', element: <SearchParticipationsPage /> },
  { path: '/private-equity/participations/:participationId', element: <PrivateEquityParticipationDetailPage /> },
  { path: '/private-equity/define', element: <DefinePrivateEquityPage /> },
  { path: '/private-equity/capture-activity', element: <CapturePrivateEquityActivityPage /> },
  { path: '/private-equity/receive', element: <ReceivePrivateEquityPage /> },

  { path: '/fees/rules', element: <FeeRulesPage /> },
  { path: '/fees/rules/new', element: <CreateFeeRulePage /> },
  { path: '/fees/rules/:ruleId/edit', element: <CreateFeeRulePage /> },
  { path: '/fees/reports/deviation', element: <FeeDeviationReportPage /> },
  { path: '/fees/reports/reclaimable', element: <ReclaimableChargesReportPage /> },
  { path: '/fees/reports/retrocession', element: <RetrocessionReportPage /> },

  { path: '/settings/theme', element: <ThemeSelectionPage /> },
  { path: '/system/signalr-logs', element: <SignalRLogsPage /> },
  { path: '/system/notification-history', element: <NotificationHistoryPage /> },

  { path: '/market-data/prices', element: <MarketPricesBrowserPage /> },
  { path: '/market-data/fx', element: <ForexRatesBrowserPage /> },
  { path: '/market-data/capture/price', element: <CapturePricePage /> },
  { path: '/market-data/capture/fx-rate', element: <CaptureFxRatePage /> },
  { path: '/market-data/position-prices', element: <PositionPricesPage /> },

  { path: '/corporate-actions/search', element: <SearchCorporateActionsPage /> },
  { path: '/corporate-actions/capture/cash-dividend', element: <CaptureCashDividendPage /> },
  { path: '/corporate-actions/capture/stock-dividend', element: <CaptureStockDividendPage /> },
  { path: '/corporate-actions/capture/return-of-capital', element: <CaptureReturnOfCapitalPage /> },
  {
    path: '/corporate-actions/capture/capital-gains-distribution',
    element: <CaptureCapitalGainsDistributionPage />,
  },
  { path: '/corporate-actions/capture/stock-split', element: <CaptureStockSplitPage /> },
  { path: '/corporate-actions/capture/reverse-stock-split', element: <CaptureReverseStockSplitPage /> },
  { path: '/corporate-actions/capture/merger', element: <CaptureMergerPage /> },
  { path: '/corporate-actions/capture/share-class-conversion', element: <CaptureShareClassConversionPage /> },
  { path: '/corporate-actions/capture/spin-off', element: <CaptureSpinOffPage /> },

  { path: '/compliance', element: <Navigate to="/compliance/dashboard" replace /> },
  { path: '/compliance/dashboard', element: <ComplianceDashboardPage /> },
  { path: '/compliance/rules', element: <ComplianceRulesDashboardPage /> },
  { path: '/compliance/rules/new', element: <CreateComplianceRulePage /> },
  { path: '/compliance/rules/:ruleId/edit', element: <CreateComplianceRulePage /> },
  { path: '/compliance/policies/new', element: <CreateCompliancePolicyPage /> },
  { path: '/compliance/policies/:policyId', element: <CompliancePolicyDetailPage /> },
  { path: '/compliance/suggest-rules', element: <SuggestComplianceRulesPage /> },

  { path: '/p/:navKey', element: <WpfNavPlaceholderPage /> },
];

/** Known redirects that should open the target tab instead of a redirect panel. */
const PATH_REDIRECTS: Record<string, string> = {
  '/holdings': '/reports/dashboards/holdings-positions',
};

export const DASHBOARD_PATH = '/';

export function normalizeDocumentPath(pathname: string): string {
  if (!pathname || pathname === '') return DASHBOARD_PATH;
  // Defensive: callers sometimes pass a full href; panel ids must be pathname-only.
  const withoutHash = pathname.split('#')[0] ?? pathname;
  const withoutQuery = withoutHash.split('?')[0] ?? withoutHash;
  const withoutTrailing =
    withoutQuery.length > 1 && withoutQuery.endsWith('/')
      ? withoutQuery.slice(0, -1)
      : withoutQuery;
  return PATH_REDIRECTS[withoutTrailing] ?? withoutTrailing;
}
